import { useEffect, useState } from 'react';
import { useTranslate } from '../../locales/hooks';

type AvatarState =
  | 'greeting'      // Приветствующее (при загрузке)
  | 'waiting'       // Ожидающее (после 20 сек)
  | 'smiling'       // Улыбающееся (обычное ожидание)
  | 'bored'         // Скучающее (после 1 мин)
  | 'thinking-1'    // Думающее (вариант 1)
  | 'thinking-2'    // Думающее (вариант 2)
  | 'thinking-3'    // Думающее (вариант 3)
  | 'thinking-4'    // Думающее (вариант 4)
  | 'eureka'        // Догадался/радостный (5 сек)
  | 'satisfied';    // Довольный собой (10 сек)

type InterviewAvatarProps = {
  isProcessing: boolean;
  lastMessageTime?: number;
  hasMessages: boolean;
  compact?: boolean;
};

const InterviewAvatar = ({ isProcessing, lastMessageTime, hasMessages, compact = false }: InterviewAvatarProps) => {
  const [state, setState] = useState<AvatarState>('greeting');
  const [thinkingVariant, setThinkingVariant] = useState<1 | 2 | 3 | 4>(1);

  const t = useTranslate();

  // Управление состоянием в зависимости от активности
  useEffect(() => {
    if (isProcessing) {
      // Случайный выбор думающего состояния
      const variant = (Math.floor(Math.random() * 4) + 1) as 1 | 2 | 3 | 4;
      setThinkingVariant(variant);
      setState(`thinking-${variant}`);
      return;
    }

    // Если только что пришло сообщение (перешли из processing в не-processing)
    if (!isProcessing && lastMessageTime) {
      setState('eureka');

      const eurekaTimer = setTimeout(() => {
        setState('satisfied');

        const satisfiedTimer = setTimeout(() => {
          setState('smiling');
        }, 10000); // 10 секунд довольного

        return () => clearTimeout(satisfiedTimer);
      }, 5000); // 5 секунд радостного

      return () => clearTimeout(eurekaTimer);
    }
  }, [isProcessing, lastMessageTime]);

  // Таймеры для состояний ожидания
  useEffect(() => {
    if (isProcessing || state === 'eureka' || state === 'satisfied') {
      return;
    }

    // Переход от greeting к waiting через 20 секунд
    if (state === 'greeting' && !hasMessages) {
      const greetingTimer = setTimeout(() => {
        setState('waiting');
      }, 20000);
      return () => clearTimeout(greetingTimer);
    }

    // Переход от waiting/smiling к bored через 60 секунд
    if ((state === 'waiting' || state === 'smiling') && !hasMessages) {
      const boredTimer = setTimeout(() => {
        setState('bored');
      }, 60000);
      return () => clearTimeout(boredTimer);
    }
  }, [state, isProcessing, hasMessages]);

  const getAvatarConfig = () => {
    switch (state) {
      case 'greeting':
        return {
          emoji: '👋',
          label: t('emoji__labelGreeting'),
          color: 'from-blue-50 to-blue-100',
          imageClass: 'greeting'
        };
      case 'waiting':
        return {
          emoji: '🤔',
          label: t('emoji__labelWaiting'),
          color: 'from-slate-50 to-slate-100',
          imageClass: 'waiting'
        };
      case 'smiling':
        return {
          emoji: '😊',
          label: t('emoji__labelSmiling'),
          color: 'from-green-50 to-green-100',
          imageClass: 'smiling'
        };
      case 'bored':
        return {
          emoji: '😴',
          label: t('emoji__labelBored'),
          color: 'from-gray-50 to-gray-100',
          imageClass: 'bored'
        };
      case 'thinking-1':
      case 'thinking-2':
      case 'thinking-3':
      case 'thinking-4':
        return {
          emoji: ['🤔', '💭', '🧐', '💡'][thinkingVariant - 1],
          label: t(`emoji__labelThinking_${thinkingVariant}`),
          color: 'from-purple-50 to-purple-100',
          imageClass: `thinking-${thinkingVariant}`
        };
      case 'eureka':
        return {
          emoji: '💡',
          label: t('emoji__labelEureka'),
          color: 'from-yellow-50 to-yellow-100',
          imageClass: 'eureka'
        };
      case 'satisfied':
        return {
          emoji: '😌',
          label: t('emoji__labelSatisfied'),
          color: 'from-emerald-50 to-emerald-100',
          imageClass: 'satisfied'
        };
      default:
        return {
          emoji: '😊',
          label: t('emoji__labelDefault'),
          color: 'from-slate-50 to-slate-100',
          imageClass: 'default'
        };
    }
  };

  const config = getAvatarConfig();

  if (compact) {
    return (
      <div className="relative shrink-0" data-name="interview-avatar" aria-label={config.label}>
        <div className={`w-[40px] h-[40px] rounded-full bg-gradient-to-br ${config.color} border-2 border-[#ddd] flex items-center justify-center overflow-hidden transition-all duration-500`}>
          <div className={`text-[24px] avatar-state-${config.imageClass} transition-transform duration-300 ${
            isProcessing ? 'animate-pulse' : ''
          }`}>
            {config.emoji}
          </div>
        </div>
        {isProcessing && (
          <div className="absolute -bottom-1 -right-1 w-[14px] h-[14px] bg-purple-500 rounded-full border-2 border-white flex items-center justify-center">
            <div className="w-[4px] h-[4px] bg-white rounded-full animate-pulse" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-[6px] md:gap-[12px] py-[20px]" data-name="interview-avatar">
      {/* Avatar container */}
      <div className="relative">
        <div className={`w-[40px] h-[40px] md:w-[80px] md:h-[80px] rounded-full bg-gradient-to-br ${config.color} border-2 border-[#ddd] flex items-center justify-center overflow-hidden transition-all duration-500`}>
          {/* Placeholder - заменить на реальные изображения */}
          <div className={`text-[24px] md:text-[48px] avatar-state-${config.imageClass} transition-transform duration-300 ${
            isProcessing ? 'animate-pulse' : ''
          }`}>
            {config.emoji}
          </div>
        </div>

        {/* Status indicator */}
        {isProcessing && (
          <div className="absolute -bottom-1 -right-1 w-[24px] h-[24px] bg-purple-500 rounded-full border-2 border-white flex items-center justify-center">
            <div className="w-[8px] h-[8px] bg-white rounded-full animate-pulse" />
          </div>
        )}
      </div>

      {/* State label */}
      <p className="font-['Manrope:Regular',sans-serif] font-normal text-[#717182] text-[11px] text-center">
        {config.label}
      </p>
    </div>
  );
}

export default InterviewAvatar;
